import {
  ESCALA,
  FAMILIA_FONTE,
  comAlfa,
  escaparTexto,
  fontesEscaladas,
  formatarVariacao,
  variacaoPercentual,
} from "./tema";

/**
 * Camadas desenhadas à mão sobre o gráfico: véu translúcido da imagem de
 * fundo, setas de variação, selos de porcentagem e caixas de texto.
 *
 * REGRA QUE NÃO PODE SER QUEBRADA: todo desenho sai daqui, a partir de
 * `chart.events.render`.
 *
 * O motivo está em como o getSVG() funciona: ele não serializa o SVG da
 * tela — monta um gráfico NOVO, fora de vista, a partir das mesmas opções,
 * e serializa esse. Como `events.render` faz parte das opções, ele roda
 * também na cópia e o desenho entra na imagem. Qualquer coisa desenhada de
 * fora (num useEffect, mexendo em chartRef.current.chart.renderer) existe
 * só no preview e some da imagem exportada, sem erro nenhum.
 *
 * Pelo mesmo motivo o spec viaja dentro das opções (`rbSpec`) em vez de
 * vir por closure: a cópia recebe as opções, não o escopo onde o handler
 * foi criado.
 */

/* ------------------------------------------------------------------ *
 * Âncoras
 * ------------------------------------------------------------------ */

function resolverAncora(chart, ancora = {}) {
  // Ancorado num ponto da série: acompanha a barra quando os dados mudam.
  if (typeof ancora.serie === "number" && typeof ancora.ponto === "number") {
    const ponto = chart.series?.[ancora.serie]?.points?.[ancora.ponto];
    if (ponto) {
      const forma = ponto.shapeArgs || {};
      return {
        x: chart.plotLeft + (ponto.plotX ?? forma.x ?? 0),
        y: chart.plotTop + (ponto.plotY ?? forma.y ?? 0),
      };
    }
  }
  // Fração do gráfico inteiro — serve para o que fica FORA da área de
  // plotagem, como a seta de variação, que mora na margem esquerda.
  if (typeof ancora.gx === "number" || typeof ancora.gy === "number") {
    return {
      x: (ancora.gx ?? 0.5) * chart.chartWidth,
      y: (ancora.gy ?? 0.5) * chart.chartHeight,
    };
  }
  // Fração da área de plotagem.
  return {
    x: chart.plotLeft + (ancora.fx ?? 0.5) * chart.plotWidth,
    y: chart.plotTop + (ancora.fy ?? 0.5) * chart.plotHeight,
  };
}

/* ------------------------------------------------------------------ *
 * Variações calculadas
 * ------------------------------------------------------------------ */

/** Índice negativo conta a partir do fim, como em Array.at(). */
const indiceReal = (i, total) => (i < 0 ? total + i : i);

/**
 * Resolve o texto e a direção de uma variação a partir dos DADOS.
 *
 * Enquanto o texto era fixo no modelo, trocar os números deixava a seta
 * mentindo — dizia "36,1%" para qualquer valor. Com `calculo`, o
 * percentual, o sentido da seta e a cor passam a sair da própria série:
 * subiu fica verde para cima, caiu fica vermelho para baixo.
 *
 * `de` e `para` são índices na série (negativos contam do fim). O padrão
 * compara a última categoria com a primeira, que é como as referências
 * mostram a variação do período — nelas o ano mais recente vem primeiro.
 */
function resolverVariacao(spec, cfg) {
  if (!cfg?.calculo) return null;
  const { serie = 0, de = -1, para = 0, decimais = 1 } = cfg.calculo;
  const valores = spec?.dados?.series?.[serie]?.valores || [];
  if (valores.length < 2) return null;

  const a = valores[indiceReal(de, valores.length)];
  const b = valores[indiceReal(para, valores.length)];
  const percentual = variacaoPercentual(a, b);
  if (percentual === null) return null;

  return {
    percentual,
    texto: formatarVariacao(percentual, decimais),
    direcao: percentual < 0 ? "baixo" : "cima",
    cor: percentual < 0 ? "#E03C3C" : "#22B14C",
  };
}

/* ------------------------------------------------------------------ *
 * Primitivas de desenho
 * ------------------------------------------------------------------ */

/**
 * Seta de bloco (as setas grandes de variação).
 *
 * `posicao` ({ x, y, largura, altura } em px) substitui a âncora e o tamanho
 * do spec — é como o indicador de variação centraliza a seta sobre a caixa.
 */
export function desenharSeta(chart, grupo, cfg = {}, variacao = null, posicao = null) {
  const { x, y } = posicao || resolverAncora(chart, cfg.em);
  const l = posicao?.largura ?? (cfg.largura ?? 30) * ESCALA;
  const a = posicao?.altura ?? (cfg.altura ?? 60) * ESCALA;
  const direcao = variacao?.direcao || cfg.direcao || "cima";
  const cor = cfg.corFixa || variacao?.cor || cfg.cor || "#22B14C";
  const meio = l / 2;
  // Com 42% a haste de uma seta de 60px ficava com 25px e parecia um
  // palito; a partir de metade da largura ela passa a ler como bloco.
  const haste = l * (cfg.espessura ?? 0.56);
  const ponta = a * 0.42;

  // Polígono de 7 pontos: haste retangular + cabeça triangular.
  const caminho =
    direcao === "baixo"
      ? [
          ["M", x - haste / 2, y],
          ["L", x + haste / 2, y],
          ["L", x + haste / 2, y + a - ponta],
          ["L", x + meio, y + a - ponta],
          ["L", x, y + a],
          ["L", x - meio, y + a - ponta],
          ["L", x - haste / 2, y + a - ponta],
          ["Z"],
        ]
      : [
          ["M", x - haste / 2, y + a],
          ["L", x + haste / 2, y + a],
          ["L", x + haste / 2, y + ponta],
          ["L", x + meio, y + ponta],
          ["L", x, y],
          ["L", x - meio, y + ponta],
          ["L", x - haste / 2, y + ponta],
          ["Z"],
        ];

  chart.renderer
    .path(caminho)
    .attr({ fill: cor, opacity: cfg.opacidade ?? 1, zIndex: 6 })
    .add(grupo);

  // `mostrarTexto: false` deixa a seta usar o calculo apenas para o
  // sentido e a cor, quando o percentual ja aparece numa caixa ao lado.
  const rotulo = cfg.mostrarTexto === false ? null : variacao?.texto || cfg.texto;
  if (rotulo) {
    const fontes = fontesEscaladas(chart.options?.rbSpec);
    chart.renderer
      .text(escaparTexto(rotulo), x, y + a / 2 + 5 * ESCALA)
      .attr({ "text-anchor": "middle", zIndex: 7 })
      .css({
        color: cfg.corTexto || "#FFFFFF",
        fontSize: `${cfg.tamanhoTexto ? cfg.tamanhoTexto * ESCALA : fontes.rotulo}px`,
        fontWeight: "bold",
        fontFamily: FAMILIA_FONTE,
      })
      .add(grupo);
  }
}

/**
 * Caixa de texto com borda — selos de %, notas e a caixa "Total".
 *
 * Sem `posicao` a caixa é alinhada pela esquerda na âncora do spec. Com
 * `posicao` quem chama decide onde ela fica depois de medida (o elemento é
 * devolvido já adicionado, na origem).
 */
export function desenharCaixa(chart, grupo, cfg = {}, variacao = null, posicao = null) {
  const { x, y } = posicao ? { x: 0, y: 0 } : resolverAncora(chart, cfg.em);
  const linhas = Array.isArray(cfg.linhas) ? cfg.linhas : [variacao?.texto || cfg.texto];
  const markup = linhas.filter(Boolean).map(escaparTexto).join("<br>");
  if (!markup) return null;

  const spec = chart.options?.rbSpec;
  const estilo = spec?.estilo || {};
  const moderno = estilo.visual !== "classico";
  const fontes = fontesEscaladas(spec);
  const corFundo = cfg.corFundoFixa || variacao?.cor || cfg.corFundo || "#000000";

  const caixa = chart.renderer
    .label(markup, 0, 0, "rect", null, null, false, false, "rb-caixa")
    .attr({
      fill: moderno
        ? comAlfa(corFundo, cfg.opacidade ?? estilo.translucidez ?? 0.85)
        : corFundo,
      // A borda tambem pode seguir o sinal da variacao: e o que mantem a
      // caixa branca coerente com a seta quando o numero vira negativo.
      stroke: (cfg.bordaDaVariacao && variacao?.cor) || cfg.corBorda || "#FFFFFF",
      "stroke-width": cfg.larguraBorda ?? 1,
      r: moderno ? estilo.cantos ?? 5 : 2,
      padding: (cfg.espaco ?? 4) * ESCALA,
      zIndex: 8,
    })
    .css({
      color: cfg.corTexto || "#FFFFFF",
      fontSize: `${cfg.tamanho ? cfg.tamanho * ESCALA : fontes.rotulo}px`,
      fontWeight: "bold",
      fontFamily: FAMILIA_FONTE,
    })
    .add(grupo);

  // Só dá para alinhar depois de adicionar: antes disso o rótulo ainda
  // não tem caixa medida.
  if (!posicao) caixa.align({ align: "left", x, y }, false, null);
  return caixa;
}

/* ------------------------------------------------------------------ *
 * Indicador de variação: seta + caixa com o percentual
 * ------------------------------------------------------------------ */

const mesmoCalculo = (a, b) =>
  Boolean(a && b) &&
  (a.serie ?? 0) === (b.serie ?? 0) &&
  (a.de ?? -1) === (b.de ?? -1) &&
  (a.para ?? 0) === (b.para ?? 0);

/**
 * Separa, das caixas, a que mostra o percentual da seta.
 *
 * O formato atual guarda essa caixa em `seta.caixa`. Os modelos salvos
 * antes disso (há um no banco) têm a caixa solta em `extras.caixas`, com o
 * mesmo cálculo da seta e ancorada numa fração do quadro — é assim que ela
 * é reconhecida, para que esses modelos também ganhem o alinhamento.
 */
function separarCaixaDaSeta(extras) {
  const seta = extras.seta;
  const caixas = extras.caixas || [];
  if (!seta) return { caixaSeta: null, caixas };
  if (seta.caixa) return { caixaSeta: seta.caixa, caixas };

  const indice = caixas.findIndex(
    (c) => typeof c?.em?.gx === "number" && mesmoCalculo(c.calculo, seta.calculo)
  );
  if (seta.mostrarTexto !== false || indice === -1) return { caixaSeta: null, caixas };
  return {
    caixaSeta: caixas[indice],
    caixas: caixas.filter((_, i) => i !== indice),
  };
}

/**
 * Seta e percentual como um bloco só, centralizado no espaço livre à
 * esquerda dos rótulos do eixo.
 *
 * Antes cada um tinha sua âncora em fração do quadro: a seta pelo centro e
 * a caixa pela borda esquerda. Como a largura da caixa muda com o texto
 * ("+5,0%" x "-36,1%"), os dois nunca ficavam alinhados, e a seta tinha
 * tamanho fixo pequeno demais para a margem. Aqui o tamanho sai do espaço
 * disponível, a caixa é medida depois de desenhada e as duas peças são
 * empilhadas e centralizadas juntas.
 */
function desenharIndicadorVariacao(chart, grupo, spec, seta, caixaCfg) {
  const variacaoSeta = resolverVariacao(spec, seta);
  const variacaoCaixa = resolverVariacao(spec, caixaCfg) || variacaoSeta;

  // Borda direita da zona: onde começam os rótulos do eixo (os anos).
  const rotulosEixo = chart.xAxis?.[0]?.labelGroup?.getBBox?.();
  const limiteDireito =
    rotulosEixo && rotulosEixo.width > 0
      ? rotulosEixo.x - 8 * ESCALA
      : chart.plotLeft - 30 * ESCALA;
  const limiteEsquerdo = 6 * ESCALA;
  const larguraZona = Math.max(20 * ESCALA, limiteDireito - limiteEsquerdo);
  const centroX = limiteEsquerdo + larguraZona / 2;

  const caixa = desenharCaixa(chart, grupo, caixaCfg, variacaoCaixa, { centro: true });
  const medida = caixa ? caixa.getBBox() : { width: 0, height: 0 };
  const intervalo = caixa ? 6 * ESCALA : 0;

  // A seta ocupa boa parte da zona, mas nunca menos que a caixa abaixo
  // dela nem mais alta do que a área de plotagem comporta.
  const escala = seta.escala ?? 1;
  let largura = Math.min(larguraZona * 0.72, 56 * ESCALA) * escala;
  largura = Math.max(largura, Math.min(medida.width, larguraZona));
  const alturaMaxima = chart.plotHeight - medida.height - intervalo;
  const altura = Math.max(24 * ESCALA, Math.min(largura * 1.5, alturaMaxima));

  const total = altura + intervalo + medida.height;
  const topo = chart.plotTop + Math.max(0, (chart.plotHeight - total) / 2);

  desenharSeta(chart, grupo, seta, variacaoSeta, { x: centroX, y: topo, largura, altura });
  if (caixa) {
    caixa.attr({
      x: Math.round(centroX - medida.width / 2),
      y: Math.round(topo + altura + intervalo),
    });
  }
}

/* ------------------------------------------------------------------ *
 * Variações livres (extras.variacoes)
 * ------------------------------------------------------------------ */

/** Separador entre períodos na mesma caixa: "1 ano: +29%  •  5 anos: +2%". */
const SEPARADOR_PADRAO = "  •  ";

/**
 * Seta horizontal apontando para fora (as duas setas amarelas que cercam a
 * faixa "5 anos = 19%" das referências).
 *
 * `x` é a base da haste, junto da caixa; a ponta cai a `comprimento` dali,
 * no sentido pedido.
 */
function desenharSetaLado(chart, grupo, cfg) {
  const { x, y, comprimento, espessura, cor, sentido } = cfg;
  const sinal = sentido === "esquerda" ? -1 : 1;
  const ponta = Math.min(comprimento * 0.5, espessura * 1.2);
  const meia = espessura / 2;
  const haste = espessura * 0.46;
  const fim = x + sinal * comprimento;
  const ombro = fim - sinal * ponta;

  chart.renderer
    .path([
      ["M", x, y - haste / 2],
      ["L", ombro, y - haste / 2],
      ["L", ombro, y - meia],
      ["L", fim, y],
      ["L", ombro, y + meia],
      ["L", ombro, y + haste / 2],
      ["L", x, y + haste / 2],
      ["Z"],
    ])
    .attr({ fill: cor, zIndex: 6 })
    .add(grupo);
}

/**
 * Indicadores de variação que o usuário monta no painel.
 *
 * Cada item é UMA caixa que pode reunir vários períodos ("1 ano" e "5
 * anos" lado a lado, como nas referências), com seta opcional acima ou dos
 * dois lados. Vale para qualquer família, porque o desenho não depende do
 * tipo de série — só das posições do quadro.
 *
 * Quando a cor é automática, quem manda é o PRIMEIRO período: é ele que
 * define verde de alta ou vermelho de queda, e o sentido da seta.
 */
function desenharVariacoes(chart, grupo, spec) {
  const itens = spec?.extras?.variacoes;
  if (!Array.isArray(itens) || !itens.length) return;

  for (const item of itens) {
    if (!item || item.visivel === false) continue;
    const calculadas = (item.periodos || [])
      .map((periodo) => ({ periodo, variacao: resolverVariacao(spec, { calculo: periodo }) }))
      .filter((r) => r.variacao);
    if (!calculadas.length) continue;

    const texto = calculadas
      .map(({ periodo, variacao }) => `${periodo.rotulo ? `${periodo.rotulo} ` : ""}${variacao.texto}`)
      .join(item.separador ?? SEPARADOR_PADRAO);
    const principal = calculadas[0].variacao;
    const automatica = item.corAutomatica !== false;

    const caixa = desenharCaixa(
      chart,
      grupo,
      {
        linhas: [texto],
        corFundoFixa: automatica ? undefined : item.corFundo || "#000000",
        corTexto: item.corTexto || "#FFFFFF",
        corBorda: item.corBorda || "#FFFFFF",
        larguraBorda: item.larguraBorda ?? 1,
        tamanho: item.tamanho,
        espaco: item.espaco,
        opacidade: item.opacidade,
      },
      automatica ? principal : null,
      { centro: true }
    );
    if (!caixa) continue;

    const medida = caixa.getBBox();
    const escala = item.escalaSeta ?? 1;
    const folga = 5 * ESCALA;
    const margem = 4 * ESCALA;
    const corSeta = item.corSeta || null;
    const temSeta = Boolean(item.seta) && item.seta !== "nenhuma";
    const lados = item.seta === "lados";
    const paraBaixo = (item.seta === "vertical" ? principal.direcao : item.seta) === "baixo";

    // O tamanho pedido é limitado ao que sobra na imagem: no talo do
    // controle a seta satura em vez de estourar a borda e ser cortada na
    // exportação.
    const pedido = (item.comprimentoSeta ?? (lados ? 34 : 22)) * ESCALA * escala;
    const sobraLateral = Math.max(0, (chart.chartWidth - 2 * margem - medida.width) / 2 - folga);
    const sobraVertical = Math.max(0, chart.chartHeight - 2 * margem - medida.height - folga);
    const comprimento = Math.min(pedido, lados ? sobraLateral : sobraVertical / 1.6);
    const altura = lados ? 0 : comprimento * 1.6;

    // Âncora presa ao quadro, contando a seta do lado em que ela fica.
    const preso = (valor, antes, depois, total) => {
      const minimo = margem + antes;
      const maximo = total - margem - depois;
      return minimo > maximo ? total / 2 : Math.max(minimo, Math.min(maximo, valor));
    };
    const meiaLargura = medida.width / 2 + (lados ? comprimento + folga : 0);
    const braco = temSeta && !lados ? altura + folga : 0;
    const bruto = resolverAncora(chart, item.em || { gx: 0.5, gy: 0.32 });
    const x = preso(bruto.x, meiaLargura, meiaLargura, chart.chartWidth);
    const y = preso(
      bruto.y,
      medida.height / 2 + (paraBaixo ? 0 : braco),
      medida.height / 2 + (paraBaixo ? braco : 0),
      chart.chartHeight
    );

    caixa.attr({
      x: Math.round(x - medida.width / 2),
      y: Math.round(y - medida.height / 2),
    });

    // Sem espaço para uma seta legível, fica só a caixa: um toco de dois
    // pixels ao lado do texto não lê como seta, lê como sujeira.
    if (!temSeta || comprimento < 5 * ESCALA) continue;

    if (lados) {
      // Faixa entre duas setas: o comprimento é de cada lado, não do total.
      const espessura = Math.max(8 * ESCALA, medida.height * 0.72);
      for (const sentido of ["esquerda", "direita"]) {
        desenharSetaLado(chart, grupo, {
          x: sentido === "esquerda" ? x - medida.width / 2 - folga : x + medida.width / 2 + folga,
          y,
          comprimento,
          espessura,
          cor: corSeta || principal.cor,
          sentido,
        });
      }
      continue;
    }

    // Seta de bloco: sobe no verde e desce no vermelho, encostada na caixa.
    desenharSeta(
      chart,
      grupo,
      { mostrarTexto: false, corFixa: corSeta, direcao: paraBaixo ? "baixo" : "cima" },
      automatica ? principal : null,
      {
        x,
        y: paraBaixo
          ? y + medida.height / 2 + folga
          : y - medida.height / 2 - folga - altura,
        largura: comprimento,
        altura,
      }
    );
  }
}

/** Texto simples do rodapé (a linha com asterisco). */
export function desenharRodape(chart, grupo, texto, cor = "#F0F000") {
  if (!texto) return;
  const fontes = fontesEscaladas(chart.options?.rbSpec);
  chart.renderer
    .text(escaparTexto(texto), chart.plotLeft, chart.chartHeight - 6 * ESCALA)
    .attr({ zIndex: 7 })
    .css({
      color: cor,
      fontSize: `${fontes.fonteCredito}px`,
      fontWeight: "bold",
      fontFamily: FAMILIA_FONTE,
    })
    .add(grupo);
}

/**
 * Imagem de fundo e véu de cor por cima dela.
 *
 * Duas abrangências:
 *
 * - "tudo" (padrão): a imagem cobre o quadro inteiro, inclusive atrás do
 *   título, do subtítulo e das margens. Precisa ser desenhada aqui porque
 *   o Highcharts só oferece plotBackgroundImage, que se limita à área de
 *   plotagem — não existe opção equivalente para o gráfico todo.
 * - "grafico": só atrás das colunas, via plotBackgroundImage (definido no
 *   opcoesBase).
 *
 * O véu é sempre um retângulo próprio, e não plotBackgroundColor, porque o
 * Highcharts pinta a cor ANTES da imagem — como sobreposição ela ficaria
 * por baixo e não teria efeito nenhum.
 *
 * O zIndex 0.5 põe a camada acima do fundo do gráfico e abaixo de todo o
 * resto: as séries ficam no 3 e o título no 4.
 */
function desenharFundo(chart, spec) {
  const fundo = spec?.fundoImagem;
  chart.rbFundo?.destroy();
  chart.rbFundo = null;
  if (!fundo?.dataUri) return;

  const tudo = (fundo.abrangencia ?? "tudo") === "tudo";
  const x = tudo ? 0 : chart.plotLeft;
  const y = tudo ? 0 : chart.plotTop;
  const largura = tudo ? chart.chartWidth : chart.plotWidth;
  const altura = tudo ? chart.chartHeight : chart.plotHeight;

  chart.rbFundo = chart.renderer.g("rb-fundo").attr({ zIndex: 0.5 }).add();

  if (tudo) {
    // "slice" recorta em vez de distorcer, igual ao object-fit: cover da
    // miniatura — a imagem já vem no formato certo, mas isso protege
    // fundos antigos gravados noutra proporção.
    chart.renderer
      .image(fundo.dataUri, x, y, largura, altura)
      .attr({ preserveAspectRatio: "xMidYMid slice" })
      .add(chart.rbFundo);
  }

  if (fundo.opacidade > 0) {
    chart.renderer
      .rect(x, y, largura, altura)
      .attr({ fill: comAlfa(fundo.cor || "#000000", fundo.opacidade) })
      .add(chart.rbFundo);
  }
}

/* ------------------------------------------------------------------ *
 * Funil / comparativo em seta (família 8)
 * ------------------------------------------------------------------ */

function desenharFunil(chart, grupo, spec) {
  const funil = spec?.extras?.funil;
  if (!funil) return;

  const rotular = (lado, ancoraX, ancoraY) => {
    if (!lado) return;
    const linhas = [lado.rotulo, lado.valor, lado.percentual].filter(Boolean);
    desenharCaixa(chart, grupo, {
      linhas,
      em: { gx: ancoraX, gy: ancoraY },
      corFundo: lado.corCaixa || "#000000",
      corBorda: lado.corBorda || "#FFFFFF",
      corTexto: lado.corTexto || "#FFFFFF",
      opacidade: lado.opacidade,
    });
  };

  // Na variante pizza a fatia é série de verdade; só as caixas são desenho.
  if (funil.formato === "pizza3d") {
    rotular(funil.esquerda, 0.03, 0.42);
    rotular(funil.direita, 0.6, 0.76);
    return;
  }

  const x0 = chart.plotLeft;
  const y0 = chart.plotTop;
  const largura = chart.plotWidth;
  const altura = chart.plotHeight;
  const corte = Math.max(0.1, Math.min(0.9, funil.divisao ?? 0.5));
  const xCorte = x0 + largura * corte;
  const meio = y0 + altura / 2;
  const metadeEsq = altura * 0.42;
  const metadeDir = altura * 0.2;

  chart.renderer
    .path([
      ["M", x0, meio - metadeEsq],
      ["L", xCorte, meio - metadeDir],
      ["L", xCorte, meio + metadeDir],
      ["L", x0, meio + metadeEsq],
      ["Z"],
    ])
    .attr({ fill: funil.esquerda?.cor || "#8B0000", zIndex: 5 })
    .add(grupo);

  const pontaX = x0 + largura;
  const cabeca = Math.min(largura * 0.14, 40 * ESCALA);
  chart.renderer
    .path([
      ["M", xCorte, meio - metadeDir],
      ["L", pontaX - cabeca, meio - metadeDir],
      ["L", pontaX - cabeca, meio - metadeDir * 1.9],
      ["L", pontaX, meio],
      ["L", pontaX - cabeca, meio + metadeDir * 1.9],
      ["L", pontaX - cabeca, meio + metadeDir],
      ["L", xCorte, meio + metadeDir],
      ["Z"],
    ])
    .attr({ fill: funil.direita?.cor || "#00B050", zIndex: 5 })
    .add(grupo);

  rotular(funil.esquerda, 0.03, 0.42);
  rotular(funil.direita, 0.6, 0.76);
}

/* ------------------------------------------------------------------ *
 * Texto no furo da rosca
 * ------------------------------------------------------------------ */

function desenharTextoCentro(chart, grupo, spec) {
  const cfg = spec?.extras?.centro;
  const serie = chart.series?.[0];
  if (!cfg || !serie?.center || serie.type !== "pie") return;

  const linhas = [cfg.titulo, cfg.valor].filter(Boolean);
  if (!linhas.length) return;
  // center vem relativo à área de plotagem: [x, y, diâmetro, furo].
  const cx = chart.plotLeft + serie.center[0];
  const cy = chart.plotTop + serie.center[1];
  const fontes = fontesEscaladas(spec);
  // O furo muda com a legenda e com o tamanho da rosca; a fonte acompanha o
  // diâmetro dele para o valor não invadir o anel. Em Arial negrito cada
  // caractere ocupa ~0,58 da fonte, e o texto pode usar ~80% do furo.
  const furo = serie.center[3] || serie.center[2] * 0.5;
  const caracteres = Math.max(4, String(cfg.valor || "").length);
  const tamanhoValor = Math.round(Math.min(fontes.titulo, (furo * 0.8) / (caracteres * 0.58)));
  const tamanhoTitulo = Math.round(Math.min(fontes.legenda, tamanhoValor * 0.75));
  const alturaBloco = (linhas.length > 1 ? tamanhoTitulo + 4 * ESCALA : 0) + tamanhoValor;
  let y = cy - alturaBloco / 2;

  if (cfg.titulo) {
    y += tamanhoTitulo;
    chart.renderer
      .text(escaparTexto(cfg.titulo), cx, y)
      .attr({ "text-anchor": "middle", zIndex: 7 })
      .css({
        color: cfg.corTitulo || "#D0D0D0",
        fontSize: `${tamanhoTitulo}px`,
        fontWeight: "bold",
        fontFamily: FAMILIA_FONTE,
      })
      .add(grupo);
    y += 4 * ESCALA;
  }
  if (cfg.valor) {
    y += tamanhoValor * 0.85;
    chart.renderer
      .text(escaparTexto(cfg.valor), cx, y)
      .attr({ "text-anchor": "middle", zIndex: 7 })
      .css({
        color: cfg.corValor || "#FFFFFF",
        fontSize: `${tamanhoValor}px`,
        fontWeight: "bold",
        fontFamily: FAMILIA_FONTE,
      })
      .add(grupo);
  }
}

/* ------------------------------------------------------------------ *
 * Handler único
 * ------------------------------------------------------------------ */

/**
 * Uma referência estável no escopo do módulo.
 *
 * Precisa ser estável porque o Highcharts registra handlers de evento por
 * identidade de função: criar uma a cada render empilharia um listener por
 * atualização durante a edição ao vivo.
 */
export function desenharCamadas() {
  // `this` é o gráfico — o da tela ou a cópia criada pelo getSVG.
  const chart = this;
  const spec = chart.options?.rbSpec;
  if (!spec) return;

  desenharFundo(chart, spec);

  chart.rbGrupoExtras?.destroy();
  chart.rbGrupoExtras = chart.renderer.g("rb-extras").attr({ zIndex: 6 }).add();
  const grupo = chart.rbGrupoExtras;

  const extras = spec.extras || {};

  desenharFunil(chart, grupo, spec);
  desenharTextoCentro(chart, grupo, spec);

  const { caixaSeta, caixas } = separarCaixaDaSeta(extras);
  if (extras.seta && caixaSeta) {
    desenharIndicadorVariacao(chart, grupo, spec, extras.seta, caixaSeta);
  } else if (extras.seta) {
    desenharSeta(chart, grupo, extras.seta, resolverVariacao(spec, extras.seta));
  }
  caixas.forEach((caixa) =>
    desenharCaixa(chart, grupo, caixa, resolverVariacao(spec, caixa))
  );
  desenharVariacoes(chart, grupo, spec);
  if (extras.rodape) desenharRodape(chart, grupo, extras.rodape, extras.corRodape);
}

/**
 * Anexa o spec às opções.
 * O handler de desenho já é ligado pelo opcoesBase; aqui só garantimos que
 * o spec viaje junto para ele achá-lo também na cópia do getSVG.
 */
export function comExtras(opcoes, spec) {
  return { ...opcoes, rbSpec: spec };
}
